import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

export interface PopoverProps {
  type?: "auto" | "manual" | string;
  active?: boolean;
  onActiveChange?: (active: boolean) => void;
  container?: string;
  contentProps?: any;
  backdropProps?: any;
  children?: React.ReactNode;
}

export const Popover: React.FC<PopoverProps> = (props) => {
  const {
    type,
    active,
    onActiveChange,
    children,
    container,
    contentProps,
    backdropProps,
  } = props;
  const activeRecordRef = useRef<PopoverActiveRecord>({});

  activeRecordRef.current.type = type;
  activeRecordRef.current.onActiveChange = onActiveChange;

  useEffect(() => {
    const ctKey = container ? "id:" + container : "elem:body";

    if (!activeRecord[ctKey]) {
      activeRecord[ctKey] = [];
    }

    const activeList = activeRecord[ctKey];

    if (active) {
      for (const ref of activeList) {
        if (ref.current && ref.current.type !== "manual") {
          if (ref.current.onActiveChange) {
            ref.current.onActiveChange(false);
          }
        }
      }

      if (activeList.length === 0) {
        const ct =
          (container && document.getElementById(container)) || document.body;
        /* 在capture阶段绑定事件，避免通过onClick触发active的时候，立马又触发该事件 */
        ct.addEventListener("click", handleContainerClick, true);
      }

      activeList.push(activeRecordRef);
    }

    return () => {
      const idx = activeList.indexOf(activeRecordRef);

      if (idx !== -1) {
        activeList.splice(idx, 1);
      }

      if (activeList.length === 0) {
        const ct =
          (container && document.getElementById(container)) || document.body;
        ct.removeEventListener("click", handleContainerClick, true);
      }
    };
  }, [container, active]);

  if (!active) {
    return null;
  }

  let backdrop: React.ReactNode = null;

  if (backdropProps) {
    backdrop = <PopoverBackdrop {...backdropProps} />;
  }

  return createPortal(
    <>
      {backdrop}
      <PopoverWrapper data-popover {...contentProps}>
        {children}
      </PopoverWrapper>
    </>,
    (container && document.getElementById(container)) || document.body
  );
};

export default Popover;

interface PopoverActiveRecord {
  type?: "auto" | "manual" | string;
  onActiveChange?: (active: boolean) => void;
}

const activeRecord: Record<string, React.RefObject<PopoverActiveRecord>[]> = {};

function handleContainerClick(evt: MouseEvent) {
  const container = evt.currentTarget as HTMLElement;
  let target = evt.target as Node | null;

  while (target && target !== container) {
    if (target.nodeType === Node.ELEMENT_NODE) {
      const elem = target as Element;

      if (elem.hasAttribute("data-popover")) {
        return;
      }
    }
    target = target.parentNode;
  }

  const ctKey = container.id ? "id:" + container.id : "elem:body";
  const activeList = activeRecord[ctKey] || [];

  for (const ref of activeList) {
    if (ref.current && ref.current.type !== "manual") {
      if (ref.current.onActiveChange) {
        ref.current.onActiveChange(false);
      }
    }
  }
}

const PopoverWrapper = styled("div")`
  position: absolute;
  top: 0;
  left: 0;
  display: inline-block;
  z-index: 99999;
`;

const PopoverBackdrop = styled("div")`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 99999;
`;
